import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowLeft, RotateCcw, Sparkles, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { sendBriefEmail } from "@/lib/api";
import { track, EVENTS } from "@/lib/analytics";
import { EASE_OUT_QUART } from "@/lib/motion";

/**
 * InteractiveQualifier
 * Multi-step dealership qualifier. Captures inventory size, current
 * photo handling, inventory change frequency, and rooftops. Renders a
 * recommended LotWise fit + suggested cadence + a "Request a Visit"
 * CTA that pre-fills the lead form.
 */

const QUESTIONS = [
  {
    id: "size",
    prompt: "How many vehicles are usually on your dealership lot?",
    options: [
      { value: "under_25", label: "Under 25" },
      { value: "25_75", label: "25 to 75" },
      { value: "75_150", label: "75 to 150" },
      { value: "150_plus", label: "150+" },
    ],
  },
  {
    id: "handling",
    prompt: "Who currently handles vehicle photos and listings?",
    options: [
      { value: "salespeople", label: "Salespeople between calls" },
      { value: "dedicated_internal", label: "A dedicated internal staff member" },
      { value: "outside_vendor", label: "An outside photographer or vendor" },
      { value: "nobody", label: "No one consistently" },
    ],
  },
  {
    id: "frequency",
    prompt: "How often does your inventory turn over?",
    options: [
      { value: "weekly_plus", label: "Multiple times per week" },
      { value: "weekly", label: "Roughly weekly" },
      { value: "biweekly", label: "Every 2 weeks" },
      { value: "monthly", label: "Monthly or slower" },
    ],
  },
  {
    id: "rooftops",
    prompt: "How many rooftops does your dealership operate?",
    options: [
      { value: "one", label: "One" },
      { value: "two_three", label: "2 to 3" },
      { value: "four_plus", label: "4+" },
    ],
  },
];

function computeRecommendation(answers) {
  const managedTriggers = [
    answers.size === "150_plus",
    answers.size === "75_150" && answers.frequency === "weekly_plus",
    answers.rooftops === "four_plus",
    answers.handling === "nobody",
  ];
  const managedScore = managedTriggers.filter(Boolean).length;

  const fit =
    managedScore >= 2
      ? {
          model: "Managed Inventory Service",
          modelId: "managed_inventory",
          rationale:
            "Your dealership operates at a scale or turnover rate where the LotWise operation is best carried end-to-end. This removes the internal listing drag entirely so your team can focus on selling.",
          cadence: answers.frequency === "weekly_plus" ? "Twice weekly onsite" : "Weekly onsite",
          badge: "Most complete",
        }
      : {
          model: "Scheduled Lot Service",
          modelId: "scheduled_lot",
          rationale:
            "Your dealership fits the recurring LotWise cadence model. We visit on a set rhythm, work the fresh inventory, and hold the standard on top of your existing workflow.",
          cadence:
            answers.frequency === "weekly_plus"
              ? "Weekly onsite"
              : answers.frequency === "weekly"
              ? "Weekly or twice monthly onsite"
              : answers.frequency === "biweekly"
              ? "Twice monthly onsite"
              : "Monthly onsite",
          badge: "Best fit",
        };

  return fit;
}

export default function InteractiveQualifier() {
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [emailInput, setEmailInput] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const total = QUESTIONS.length;
  const finished = stepIdx >= total;
  const progress = Math.min(1, stepIdx / total);

  const recommendation = useMemo(
    () => (finished ? computeRecommendation(answers) : null),
    [finished, answers]
  );

  const current = QUESTIONS[stepIdx];

  // Fire qualifier_completed exactly once per completion (guarded
  // against React 18 StrictMode double-effect in dev). Reset on
  // qualifier restart via the Restart button.
  const completedFired = useRef(false);
  useEffect(() => {
    if (finished && recommendation && !completedFired.current) {
      completedFired.current = true;
      track(EVENTS.QUALIFIER_COMPLETED, {
        model_id: recommendation.modelId,
        cadence: recommendation.cadence,
      });
    }
  }, [finished, recommendation]);

  function select(value) {
    if (stepIdx === 0) {
      track(EVENTS.QUALIFIER_STARTED, { first_answer: value });
    }
    setAnswers((a) => ({ ...a, [current.id]: value }));
    setStepIdx((i) => i + 1);
  }

  function back() {
    if (stepIdx > 0) setStepIdx(stepIdx - 1);
  }

  function reset() {
    setAnswers({});
    setStepIdx(0);
    setEmailInput("");
    setEmailSent(false);
    completedFired.current = false;
  }

  async function handleEmailBrief(e) {
    e.preventDefault();
    if (emailSending || !recommendation) return;
    setEmailSending(true);
    try {
      await sendBriefEmail({
        recipient_email: emailInput.trim(),
        model_id: recommendation.modelId,
        cadence: recommendation.cadence,
        answers,
      });
      setEmailSent(true);
      track(EVENTS.BRIEF_EMAILED, {
        model_id: recommendation.modelId,
        via: "qualifier_inline",
      });
      toast.success("Your Dealer Brief is on its way.", {
        description: `Sent to ${emailInput.trim()}. Check your inbox in a minute or two.`,
      });
    } catch (err) {
      toast.error("Could not send the brief.", {
        description:
          err?.response?.data?.detail ||
          "Please try again in a moment or contact hello@lotwiseauto.ca.",
      });
    } finally {
      setEmailSending(false);
    }
  }

  return (
    <section
      data-testid="dealer-qualifier"
      id="dealer-qualifier"
      className="relative overflow-hidden bg-[#050609] text-white"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(10,181,255,0.18),transparent_55%),radial-gradient(circle_at_80%_90%,rgba(79,13,255,0.22),transparent_55%)]"
      />
      <div className="container-lw section-lw relative">
        <div className="mb-12 grid gap-6 md:grid-cols-[1fr_auto] md:items-end md:mb-16">
          <div>
            <div className="eyebrow text-[#0AB5FF]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#0AB5FF]" />
              Interactive Dealer Qualifier
            </div>
            <h2 className="mt-5 h-display text-4xl md:text-5xl lg:text-6xl">
              Four questions, <br />
              <span className="text-gradient-lw">a real recommendation.</span>
            </h2>
          </div>
          <p className="max-w-md text-[15px] leading-relaxed text-white/60">
            Tell us how your dealership actually runs. LotWise will
            recommend the service model and visit cadence that fits, and
            hand you straight into a scheduled call.
          </p>
        </div>

        <div className="relative overflow-hidden rounded-[6px] border border-white/12 bg-white/[0.03] backdrop-blur-sm">
          {/* Progress bar */}
          <div className="h-1 w-full bg-white/5">
            <motion.div
              data-testid="qualifier-progress"
              className="h-full origin-left bg-gradient-to-r from-[#0AB5FF] to-[#4F0DFF]"
              initial={false}
              animate={{ scaleX: progress }}
              transition={{ duration: 0.6, ease: EASE_OUT_QUART }}
              style={{ transformOrigin: "left" }}
            />
          </div>

          <div className="grid gap-10 p-8 md:grid-cols-[1fr_1.2fr] md:gap-14 md:p-14">
            {/* Left: status */}
            <div className="border-b border-white/10 pb-8 md:border-b-0 md:border-r md:pr-10 md:pb-0">
              <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-white/40">
                {finished ? "Recommendation" : `Question ${stepIdx + 1} of ${total}`}
              </div>
              <div className="mt-3 font-display text-2xl font-semibold tracking-tight md:text-3xl">
                {finished
                  ? "Here is your LotWise fit."
                  : current?.prompt}
              </div>

              <div className="mt-8 space-y-2 text-xs text-white/45">
                {QUESTIONS.map((q, i) => (
                  <div
                    key={q.id}
                    className={`flex items-center gap-3 ${
                      i === stepIdx && !finished
                        ? "text-white"
                        : answers[q.id]
                        ? "text-white/80"
                        : ""
                    }`}
                  >
                    <span
                      className={`grid h-5 w-5 place-items-center rounded-full border text-[9px] font-semibold ${
                        answers[q.id]
                          ? "border-[#0AB5FF] bg-[#0AB5FF]/20 text-[#0AB5FF]"
                          : i === stepIdx && !finished
                          ? "border-white text-white"
                          : "border-white/20 text-white/40"
                      }`}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="truncate">{q.prompt}</span>
                  </div>
                ))}
              </div>

              {/* What happens next — visible only on the result screen so the
                  left column reads as fully composed at desktop widths. */}
              {finished ? (
                <div className="mt-10 border-t border-white/10 pt-6">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-white/50">
                    What happens next
                  </div>
                  <ol className="mt-4 space-y-3 text-xs text-white/60">
                    {[
                      "Get your dealership plan, or email the brief to ownership.",
                      "LotWise reviews your dealership rhythm and confirms cadence.",
                      "First onsite visit is scheduled inside your priority region.",
                    ].map((line, idx) => (
                      <li key={line} className="flex gap-3">
                        <span className="mt-0.5 font-display text-[10px] font-semibold tracking-[0.22em] text-[#0AB5FF]">
                          {String(idx + 1).padStart(2, "0")}
                        </span>
                        <span className="leading-relaxed">{line}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              ) : null}
            </div>

            {/* Right: content */}
            <div className="min-h-[320px]">
              <AnimatePresence mode="wait">
                {!finished ? (
                  <motion.div
                    key={current.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.4, ease: EASE_OUT_QUART }}
                    className="grid gap-3"
                  >
                    {current.options.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        data-testid={`qualifier-option-${current.id}-${opt.value}`}
                        onClick={() => select(opt.value)}
                        className="group flex items-center justify-between rounded-[4px] border border-white/12 bg-white/[0.02] px-5 py-4 text-left transition hover:border-[#0AB5FF] hover:bg-white/[0.05]"
                      >
                        <span className="text-[15px] font-medium text-white">
                          {opt.label}
                        </span>
                        <span className="grid h-8 w-8 place-items-center rounded-full border border-white/15 text-white/50 transition group-hover:border-[#0AB5FF] group-hover:text-white">
                          <ArrowUpRight className="h-4 w-4" />
                        </span>
                      </button>
                    ))}

                    {stepIdx > 0 && (
                      <button
                        type="button"
                        data-testid="qualifier-back"
                        onClick={back}
                        className="mt-3 inline-flex items-center gap-2 self-start text-xs font-semibold uppercase tracking-[0.22em] text-white/50 hover:text-white"
                      >
                        <ArrowLeft className="h-3.5 w-3.5" /> Back
                      </button>
                    )}
                  </motion.div>
                ) : (
                  <motion.div
                    key="result"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: EASE_OUT_QUART }}
                    data-testid="qualifier-result"
                  >
                    <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-white/80">
                      <Sparkles className="h-3 w-3 text-[#0AB5FF]" />
                      {recommendation.badge}
                    </div>
                    <h3
                      data-testid="qualifier-result-model"
                      className="mt-4 h-display text-4xl md:text-5xl"
                    >
                      <span className="text-gradient-lw">
                        {recommendation.model}
                      </span>
                    </h3>
                    <p className="mt-4 max-w-lg text-[15px] leading-relaxed text-white/70">
                      {recommendation.rationale}
                    </p>

                    <div className="mt-6 grid gap-3 rounded-[4px] border border-white/10 bg-black/40 p-5 sm:grid-cols-3">
                      <ResultStat
                        k="Service model"
                        v={recommendation.model}
                      />
                      <ResultStat
                        k="Suggested cadence"
                        v={recommendation.cadence}
                      />
                      <ResultStat
                        k="Operators per visit"
                        v="Two, minimum"
                      />
                    </div>

                    <div className="mt-8 flex flex-wrap items-center gap-3">
                      <Link
                        to="/request-a-visit"
                        state={{ preselect: recommendation.modelId, prefill: answers }}
                        data-testid="qualifier-cta-request"
                        className="btn-primary-lw"
                      >
                        Get My Dealership Plan
                        <ArrowUpRight className="h-4 w-4" />
                      </Link>
                      <Link
                        to="/dealer-brief"
                        state={{
                          preselect: recommendation.modelId,
                          prefill: answers,
                          cadence: recommendation.cadence,
                        }}
                        data-testid="qualifier-cta-brief"
                        className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/[0.04] px-5 py-3 text-sm font-semibold text-white transition hover:border-white hover:bg-white/[0.08]"
                      >
                        Open the Dealer Brief
                        <ArrowUpRight className="h-3.5 w-3.5" />
                      </Link>
                      <button
                        type="button"
                        onClick={reset}
                        data-testid="qualifier-reset"
                        className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-sm font-semibold text-white/80 transition hover:border-white hover:text-white"
                      >
                        <RotateCcw className="h-3.5 w-3.5" /> Restart
                      </button>
                    </div>

                    {/* Email the brief */}
                    <div
                      className="mt-8 rounded-[6px] border border-white/12 bg-black/40 p-5"
                      data-testid="qualifier-email-brief-panel"
                    >
                      <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.24em] text-white/60">
                        <Mail className="h-3.5 w-3.5 text-[#0AB5FF]" />
                        Email the brief to ownership
                      </div>
                      <p className="mt-2 text-sm text-white/60">
                        We will send this Dealer Brief straight to the
                        recipient&apos;s inbox with a link back to the full
                        personalised page. Ready to forward.
                      </p>
                      {emailSent ? (
                        <div
                          data-testid="qualifier-email-sent"
                          className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#0AB5FF]/15 px-4 py-2 text-sm font-semibold text-[#0AB5FF]"
                        >
                          <Send className="h-3.5 w-3.5" />
                          Brief sent to {emailInput.trim()}
                        </div>
                      ) : (
                        <form
                          onSubmit={handleEmailBrief}
                          className="mt-4 flex flex-col gap-2 sm:flex-row"
                          data-testid="qualifier-email-form"
                        >
                          <input
                            type="email"
                            required
                            placeholder="principal@yourdealership.ca"
                            value={emailInput}
                            onChange={(e) => setEmailInput(e.target.value)}
                            data-testid="qualifier-email-input"
                            className="flex-1 rounded-full border border-white/15 bg-white/[0.03] px-5 py-3 text-sm text-white placeholder:text-white/40 focus:border-[#0AB5FF] focus:outline-none focus:ring-1 focus:ring-[#0AB5FF]"
                          />
                          <button
                            type="submit"
                            disabled={emailSending || !emailInput.trim()}
                            data-testid="qualifier-email-submit"
                            className="btn-primary-lw text-sm disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {emailSending ? "Sending..." : "Email the brief"}
                            <Send className="h-3.5 w-3.5" />
                          </button>
                        </form>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ResultStat({ k, v }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/50">
        {k}
      </div>
      <div className="mt-1 text-sm font-semibold text-white">{v}</div>
    </div>
  );
}
